import dayjs from '../../utils/dayjs';
import { FormLayout, Modal, TextField, Form, Select, ChoiceList, Checkbox, Button } from '@shopify/polaris';
import { useEffect, useRef, useState } from 'react';
import helpers from 'helpers';
import { lengthLessThan, notEmpty, useField, useForm } from '@shopify/react-form';
import __ from 'languages/index';

import { useMutateGetJourneys } from 'queries/journey.query';
import { TypedPromotion, useMutateCreatePromotion, useMutateGetPromotion, useUpdatePromotion } from 'queries/promotion.query';

/**
 *   Create upload Modal for Notification
 */

// eslint-disable-next-line import/no-anonymous-default-export

export default function PickupTimeRuleModal({
  onClose,
  show,
  currentPromotionID,
}: {
  onClose: Function;
  show: boolean;
  currentPromotionID?: bigint;
}) {
  const { mutateAsync: createEntity, isPending: creating, isSuccess: creatingSuccess } = useMutateCreatePromotion();
  const { mutateAsync: updateEntity, isPending: updating, isSuccess: updateSuccess } = useUpdatePromotion();
  const { mutateAsync: getPromotion, data: promotionData } = useMutateGetPromotion();
  const { mutateAsync: getJourneyData, data: journeyData } = useMutateGetJourneys();

  useEffect(() => {
    getJourneyData({
      limit: 10,
      journey_status: 1,
    });
  }, []);

  // 2. Chuyển đổi JSON thành options cho component Select
  const journeyOptions = [
    { label: 'Mọi hành trình', value: '' }, // Thêm một lựa chọn mặc định
    ...(journeyData || []).map(({ id, journey_name }) => ({
      label: String(journey_name),
      value: String(id),
    })),
  ];

  /** Hết code lấy user role ra ... */
  const [entity, setEntity] = useState<TypedPromotion>(null);
  useEffect(() => {
    if (promotionData) setEntity(promotionData);
  }, [promotionData]);

  useEffect(() => {
    if (currentPromotionID) {
      getPromotion(currentPromotionID);
    } else {
      setEntity(null);
    }
  }, [currentPromotionID]);

  const useFields = {
    promotion_name: useField<string>({
      value: entity?.promotion_name ?? '',
      validates: [
        notEmpty('Không được để trống trường này.'),
        lengthLessThan(250, 'No more than 250 characters.'),
        (inputValue) => {
          if (inputValue.length < 2) {
            return 'Your field is too short!';
          }
        },
      ],
    }),
    promotion_video_link: useField<string>({
      value: entity?.promotion_video_link ?? '',
      validates: [
        notEmpty('Không được để trống trường này.'),
        lengthLessThan(250, 'No more than 250 characters.'),
        (inputValue) => {
          if (!helpers.isLink(inputValue)) {
            return 'Your link is invalid!';
          }
        },
      ],
    }),

    event_type: useField<string>({
      value: entity?.event_type || 'ONBOARD',
      validates: [notEmpty('Không được để trống trường này.')],
    }),

    journey_id: useField<string>({
      value: entity?.journey_id,
      validates: [],
    }),

    is_active: useField<number>({
      value: Number(entity?.is_active ?? 1),
      validates: [],
    }),

    weekday: useField<string>({
      value: entity?.promotion_schedule?.weekday || 'ALL',
      validates: [],
    }),

    start_time: useField<string>({
      value: entity?.promotion_schedule?.start_time ? helpers.numberToTime(entity?.promotion_schedule?.start_time) : '00:00',
      validates: [
        (inputVal) => {
          if (!helpers.isValidTime(inputVal)) return 'Không đúng định dạng giờ HH:mm';
        },
      ],
    }),

    end_time: useField<string>({
      value: entity?.promotion_schedule?.end_time ? helpers.numberToTime(entity?.promotion_schedule?.end_time) : '23:59',
      validates: [
        (inputVal) => {
          if (!helpers.isValidTime(inputVal)) return 'Không đúng định dạng giờ HH:mm';
        },
      ],
    }),

    start_date: useField<string>({
      value: entity?.promotion_schedule?.start_date ? dayjs(new Date(entity?.promotion_schedule?.start_date)).format('YYYY-MM-DD') : '',
      validates: [
        (inputVal) => {
          if (inputVal && !helpers.isDate(inputVal)) return 'Không đúng định dạng ngày tháng năm.';
        },
      ],
    }),
    end_date: useField<string>({
      value: entity?.promotion_schedule?.end_date ? dayjs(new Date(entity?.promotion_schedule?.end_date)).format('YYYY-MM-DD') : '',
      validates: [
        (inputVal) => {
          if (inputVal && !helpers.isDate(inputVal)) return 'Không đúng định dạng ngày tháng năm.';
        },
      ],
    }),

    special_date: useField<number>({
      value: Number(entity?.promotion_schedule?.special_date ?? 0),
      validates: [],
    }),
  };

  const { fields, submit, dirty, reset } = useForm({
    fields: useFields,

    async onSubmit(values) {
      try {
        // create new
        if (!currentPromotionID) {
          await createEntity({
            promotion_name: values.promotion_name,
            promotion_video_link: values.promotion_video_link,
            event_type: values.event_type,
            journey_id: values.journey_id || null,
            is_active: helpers.parseNumeric(values.is_active),

            promotion_schedule: {
              weekday: values.weekday as any,
              start_time: helpers.parseNumeric(values.start_time),
              end_time: helpers.parseNumeric(values.end_time),
              special_date: values.special_date,
              start_date: values.start_date ? values.start_date + 'T00:00:00.000Z' : null,
              end_date: values.end_date ? values.end_date + 'T00:00:00.000Z' : null,
            },
          });
        } else {
          await updateEntity({
            id: String(currentPromotionID),
            promotion_name: values.promotion_name,
            promotion_video_link: values.promotion_video_link,
            event_type: values.event_type,
            journey_id: values.journey_id || null,
            is_active: helpers.parseNumeric(values.is_active),

            promotion_schedule: {
              weekday: values.weekday as any,
              start_time: helpers.parseNumeric(values.start_time),
              end_time: helpers.parseNumeric(values.end_time),
              special_date: values.special_date,
              start_date: values.start_date ? values.start_date + 'T00:00:00.000Z' : null,
              end_date: values.end_date ? values.end_date + 'T00:00:00.000Z' : null,
            },
          });
        }
      } catch (e: any) {
        if (e.params !== undefined) {
          if (e.params.field !== undefined) {
            useFields[e.params.field].setError(__('check_this_field_again'));
          }
        }
      }
      return { status: 'success' };
    },
  });
  /* End form */

  /** Tự động format giờ */
  const prevDigitsRef = useRef('');
  const handleTimeChange = (raw) => {
    const digits = raw.replace(/\D/g, '').slice(0, 4);
    const prevDigits = prevDigitsRef.current;
    const isAdding = digits.length > prevDigits.length;

    let formatted = raw;

    if (isAdding) {
      // chỉ format khi đang gõ thêm
      formatted = digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;
    } else {
      // nếu đang xóa: giữ nguyên dấu ':' nếu nó đang có trong raw
      if (raw.includes(':')) {
        formatted = raw;
      } else {
        // fallback: format lại theo digits để tránh lộn xộn
        formatted = digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;
      }
    }

    prevDigitsRef.current = digits;
    return formatted;
  };

  // const allErrors = Object.entries(useFields)
  //   .filter(([_, field]) => field.error)
  //   .map(([key, field]) => `${key}: ${field.error}`);

  useEffect(() => {
    if (updateSuccess || creatingSuccess) {
      helpers.resetForm(useFields);
      onClose();
    }
  }, [updateSuccess, creatingSuccess]);

  useEffect(() => {
    if (!currentPromotionID) {
      helpers.resetForm(useFields);
      setEntity(null);
    }
  }, [currentPromotionID]);

  const Actual_page = (
    <Modal
      open={show}
      onClose={() => onClose()}
      title={currentPromotionID ? 'Cập nhật chiến dịch' : 'Tạo chiến dịch'}
      primaryAction={{
        content: currentPromotionID ? 'Cập nhật' : 'Tạo',
        disabled: !dirty,
        loading: updating || creating,
        onAction: submit,
      }}
      secondaryActions={[
        {
          content: 'Close',
          onAction: () => onClose(),
        },
      ]}
    >
      <Modal.Section>
        <Form onSubmit={submit}>
          <FormLayout>
            <TextField autoComplete="off" label="Tên chiến dịch" {...fields.promotion_name} />
            <TextField autoComplete="off" label="Video Link" placeholder="https://" {...fields.promotion_video_link} />
            <ChoiceList
              title="Video này sẽ phát lúc?"
              choices={[
                {
                  label: 'ONBOARD (bắt đầu hành trình)',
                  value: 'ONBOARD',
                  helpText: 'Khách cuối cùng trong một hành trình đã có mặt hết trên xe',
                },
                {
                  label: 'OFFBOARD (kết thúc hành trình)',
                  value: 'OFFBOARD',
                  helpText: 'Phát lúc khách hàng đầu tiên chuẩn bị xuống xe.',
                },
              ]}
              selected={[fields.event_type.value]}
              onChange={(v) => fields.event_type.onChange(v[0])}
            />

            <Select
              label="Phát vào thứ mấy?"
              options={[
                {
                  label: 'Mọi ngày',
                  value: 'ALL',
                },
                {
                  label: 'Thứ hai',
                  value: 'MON',
                },
                {
                  label: 'Thứ ba',
                  value: 'TUE',
                },
                {
                  label: 'Thứ tư',
                  value: 'WED',
                },
                {
                  label: 'Thứ năm',
                  value: 'THU',
                },
                {
                  label: 'Thứ sáu',
                  value: 'FRI',
                },
                {
                  label: 'Thứ bảy',
                  value: 'SAT',
                },
                {
                  label: 'Chúa nhật',
                  value: 'SUN',
                },
              ]}
              onChange={(v) => fields.weekday.onChange(v)}
              value={fields.weekday.value}
            />

            <FormLayout.Group>
              <TextField
                autoComplete="off"
                label="Thời gian bắt đầu"
                {...fields.start_time}
                onChange={(v) => fields.start_time.onChange(handleTimeChange(v))}
                clearButton
                onClearButtonClick={() => fields.start_time.onChange('')}
              />
              <TextField
                autoComplete="off"
                label="Thời gian kết thúc"
                {...fields.end_time}
                onChange={(v) => fields.end_time.onChange(handleTimeChange(v))}
                clearButton
                onClearButtonClick={() => fields.end_time.onChange('')}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <TextField
                autoComplete="off"
                label="Ngày bắt đầu"
                placeholder="2025-12-30"
                clearButton
                onClearButtonClick={() => fields.start_date.onChange('')}
                labelAction={{
                  content: 'Hôm nay',
                  onAction: () => {
                    fields.start_date.onChange(dayjs(new Date()).format('YYYY-MM-DD'));
                  },
                }}
                {...fields.start_date}
              />
              <TextField
                autoComplete="off"
                label="Ngày kết thúc"
                placeholder="2025-12-30"
                clearButton
                onClearButtonClick={() => fields.end_date.onChange('')}
                labelAction={{
                  content: 'Hôm nay',
                  onAction: () => {
                    fields.end_date.onChange(dayjs(new Date()).format('YYYY-MM-DD'));
                  },
                }}
                {...fields.end_date}
              />
            </FormLayout.Group>

            <Checkbox
              label="Ưu tiên dành cho mồng 1 và hôm rằm?"
              helpText=""
              checked={Boolean(fields.special_date.value === 1)}
              onChange={(e) => fields.special_date.onChange(Number(e))}
            />

            <Select
              label="Trong hành trình nào?"
              options={journeyOptions}
              onChange={(selectedId) => fields.journey_id.onChange(selectedId)}
              value={fields.journey_id.value} // Giá trị của Select là ID (lấy từ trường _geo)
              error={fields.journey_id.error} // Hiển thị lỗi từ trường _geo
            />

            <Checkbox
              label="Cho phép hoạt động"
              helpText=""
              checked={Boolean(fields.is_active.value === 1)}
              onChange={(e) => fields.is_active.onChange(Number(e))}
            />
          </FormLayout>
        </Form>
      </Modal.Section>
    </Modal>
  );

  return <>{Actual_page}</>;
}

